// Canvas — react-konva implementation.
// Why: fixed-size poster artboards (1080×1440), not an infinite whiteboard.
// Konva gives precise artboard control, a built-in Transformer (select/scale/rotate), zIndex layering,
// and toDataURL, which both region editing and export need.
//
// Details:
//   - CJK per-glyph rendering: every Text node sets an explicit fontFamily (Space Grotesk + Noto Sans SC),
//     and we batchDraw once webfonts are ready (fonts.ready) — otherwise glyph widths are measured with
//     the fallback font and wrapping breaks.
//   - Chrome colors (placeholder/Transformer) come from design tokens (oklch resolved via getComputedStyle).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Text, Image as KImage, Transformer, Path, Group } from 'react-konva';
import type Konva from 'konva';
import { useT } from '../i18n';
import { useTheme } from '../theme';
import { AsciiLoader, fmtElapsed, useNow } from './loading';
import type { CanvasObject, CanvasState, JobRecord } from '../types';
import { collectDescendants } from '../lib/layout';
import { LINE_HEIGHT_RATIO } from '../lib/textMetrics';
import { ICON_PATHS } from '../lib/iconData'; // SPEC-457 M1: bundled lucide path data（零外部请求）
import { normalizePathData } from '../lib/pathNorm'; // SPEC-465 M1: arc-flag 归一化（杀 Konva 9.3.22 compact-flag 误 tokenize 类；幂等、规范形字节稳定）
import { iconStrokePx } from '../lib/iconOptics'; // SPEC-464 M1.3: 图标光学描边曲线（worker 同源）
import { fitGeom } from '../lib/imageFit'; // SPEC-464 M2: 图片 fit 拟合几何（worker facts/仲裁同源）

/** Unified font stack for on-canvas text — sans throughout, no serifs. */
const CANVAS_FONT = '"Space Grotesk", "Noto Sans SC", -apple-system, "PingFang SC", sans-serif';

/** Read and resolve a design token (the browser resolves oklch into a canvas-usable color). */
function cssColor(name: string, fallback: string): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

function useHtmlImage(src: string | null | undefined): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const el = new window.Image();
    el.crossOrigin = 'anonymous'; // PNG export requires an untainted canvas; failure only affects export
    el.onload = () => setImg(el);
    el.onerror = () => setImg(null);
    el.src = src;
    return () => { el.onload = null; el.onerror = null; };
  }, [src]);
  return img;
}

function ImageNode({ obj, common, colors }: { obj: CanvasObject; common: any; colors: { accent: string; danger: string } }) {
  const img = useHtmlImage(obj.src);
  if (obj.pending || !obj.src) {
    // Generation-in-progress placeholder: Konva only draws a selectable dashed frame; status text lives in a
    // DOM overlay (GenStatusOverlay) — CSS animation is cheaper than Konva redraws and allows mono/tabular-nums.
    return (
      <Rect
        id={obj.id}
        x={obj.x} y={obj.y} width={obj.w} height={obj.h}
        fill="rgba(128,128,128,0.10)" stroke={obj.error ? colors.danger : colors.accent}
        strokeWidth={1.5} dash={[6, 4]} cornerRadius={obj.radius || 4}
        visible={obj.visible !== false}
        onClick={common.onClick} onTap={common.onTap}
      />
    );
  }
  // Shape cropping: radius>0 = rounded corners, mask='ellipse' = ellipse.
  // Konva.Image has no cornerRadius, so wrap in a Group + clipFunc; id/drag/transform/click all live on the Group.
  // SPEC-464 M2: fit 拟合渲染 — cover = 源像素域居中裁剪（Konva cropX/Y/Width/Height，按 natural
  // size 计算）、contain = 等比缩放居中（绘制尺寸+盒内偏移）、fill/字段缺席 = 现状拉伸
  //（off 路径 byte-identical：fit 缺席不挂任何新 props）。源尺寸优先位图实测 natural size
  //（渲染面 SoT），未加载时回退 JOB_UPDATE pin 的 obj.src_w/h（worker 审计面同源值）。
  // 导出/缩略图/critique 截图同走本组件 → 自动同源（spec M2.2）。
  const fitMode = obj.fit === 'cover' || obj.fit === 'contain' ? obj.fit : undefined;
  const fitSrcW = img?.naturalWidth || obj.src_w || 0;
  const fitSrcH = img?.naturalHeight || obj.src_h || 0;
  const geom = fitMode ? fitGeom(fitMode, fitSrcW, fitSrcH, obj.w, obj.h) : null;
  const fitProps = geom
    ? fitMode === 'cover'
      ? { cropX: geom.cropX, cropY: geom.cropY, cropWidth: geom.cropW, cropHeight: geom.cropH }
      : { width: geom.drawW, height: geom.drawH }
    : {};
  const containPos = geom && fitMode === 'contain' ? { dx: geom.offsetX, dy: geom.offsetY } : null;
  const needClip = (obj.radius ?? 0) > 0 || obj.mask === 'ellipse';
  // SPEC-464 M2 review 必改 4（T-777 r3）：交互节点几何恒 = 对象盒，拟合几何只落绘制子节点。
  // contain 的盒内偏移不能写进承载 onDragEnd/onTransformEnd 回写的节点——回写读节点几何，
  // x=obj.x+dx 会让每次拖动累加一次居中偏移、缩放把裁剪后绘制尺寸写回对象盒。与 clip
  // 分支同款纪律：needClip || containPos → 统一 Group 包裹（Group=盒=id/交互/回写源）。
  // 裸 KImage 路径只服务无 clip 且无 contain 偏移（cover crop / fill / fit 缺席）。
  if (!needClip && !containPos) {
    return <KImage image={img} x={obj.x} y={obj.y} width={obj.w} height={obj.h} rotation={obj.rotation ?? 0} {...common} visible={obj.visible !== false} {...fitProps} />;
  }
  return (
    <Group
      id={obj.id}
      x={obj.x} y={obj.y} width={obj.w} height={obj.h}
      rotation={obj.rotation ?? 0}
      clipFunc={needClip ? (ctx: any) => {
        const w = obj.w, h = obj.h;
        ctx.beginPath();
        if (obj.mask === 'ellipse') {
          ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        } else {
          const r = Math.min(obj.radius ?? 0, w / 2, h / 2);
          ctx.moveTo(r, 0);
          ctx.lineTo(w - r, 0); ctx.arcTo(w, 0, w, r, r);
          ctx.lineTo(w, h - r); ctx.arcTo(w, h, w - r, h, r);
          ctx.lineTo(r, h); ctx.arcTo(0, h, 0, h - r, r);
          ctx.lineTo(0, r); ctx.arcTo(0, 0, r, 0, r);
        }
        ctx.closePath();
      } : undefined}
      {...common}
      visible={obj.visible !== false}
    >
      {/* contain 无 clip：铺满盒的全透明 Rect 打开命中区（frame/icon 分支 fill=transparent 同思路）——
          命中区/Transformer 框 = 对象盒（letterbox 可点选），选中框不收缩到内容绘制区；
          子绘制节点保持 listening=false 不参与命中。clip 分支不铺 = 现状零变更（范围外）。 */}
      {!needClip && containPos && <Rect width={obj.w} height={obj.h} fill="transparent" />}
      <KImage image={img ?? undefined} width={obj.w} height={obj.h} listening={false} {...(containPos ? { x: containPos.dx, y: containPos.dy } : {})} {...fitProps} />
    </Group>
  );
}

/**
 * Generating/failed overlay (beui agent-progress style):
 * driven by real state — job.status (queued/running) picks the verb, job.created_at drives the timer;
 * when the job record is unavailable (stale data/race), show only the verb — never fabricate a timer.
 * pointer-events: none — clicks fall through to the Konva dashed frame (select/delete still work).
 */
function GenStatusOverlay({ obj, job, scale }: { obj: CanvasObject; job?: JobRecord; scale: number }) {
  const t = useT();
  const now = useNow(250);
  const style: React.CSSProperties = {
    left: obj.x * scale, top: obj.y * scale, width: obj.w * scale, height: obj.h * scale,
  };
  if (obj.error) {
    return (
      <div className="gen-overlay" style={style}>
        <span className="gen-overlay-line gen-overlay-err">! {t('canvas.genFailed')}</span>
      </div>
    );
  }
  const verb = job?.status === 'queued' ? t('canvas.queued') : t('canvas.generating');
  const elapsed = job?.created_at ? now - job.created_at : null;
  return (
    <div className="gen-overlay" style={style}>
      <span className="gen-overlay-line">
        <AsciiLoader />
        <span>{verb}</span>
        {elapsed != null && <span className="gen-timer">{fmtElapsed(elapsed)}</span>}
      </span>
    </div>
  );
}

// ===== SPEC-409: agent ghost cursor =====
// Agent-presence overlay: a small monochrome SVG mouse that "visits" freshly born objects
// (births stamped as _born by applyOps), camps on pending image placeholders while jobs run,
// pulses once when a job lands, shakes and fades on error, fades out when idle. DOM sibling
// of GenStatusOverlay — CSS transform transitions are cheaper than Konva redraws.
// pointer-events:none: never intercepts canvas interaction. Not rendered in preview mode.

const GHOST_FRESH_MS = 8000; // an object counts as "just born" within this window
const GHOST_DWELL_MS = 700;  // stay time per visited object
const GHOST_PULSE_MS = 420;  // click pulse when a job lands
const GHOST_SHAKE_MS = 620;  // error shake

type GhostPhase = 'hidden' | 'visit' | 'camp' | 'pulse' | 'shake';

function GhostCursor({ objects, jobs, scale, preview, bounds }: { objects: CanvasObject[]; jobs: Record<string, JobRecord>; scale: number; preview: boolean; bounds: { w: number; h: number } }) {
  const t = useT();
  const [phase, setPhase] = useState<GhostPhase>('hidden');
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [tick, setTick] = useState(0); // advances the visit sequence; re-arms the effect after pulse/shake
  const visitedRef = useRef<Set<string>>(new Set());      // `${id}:${_born}` — each birth visited exactly once
  const prevJobsRef = useRef<Record<string, string>>({}); // job-status snapshot for transition detection
  const busyRef = useRef(false);                          // pulse/shake in flight — don't retarget mid-animation
  const timerRef = useRef<number | null>(null);           // single timer lane; cleared on unmount

  useEffect(() => () => { if (timerRef.current != null) window.clearTimeout(timerRef.current); }, []);

  useEffect(() => {
    if (preview) { setPhase('hidden'); return; }
    const now = Date.now();
    const schedule = (ms: number, cb: () => void) => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => { timerRef.current = null; cb(); }, ms);
    };
    // setPos only when the target actually moved — JOB_UPDATE polling recreates `jobs`
    // identity every tick; a fresh {x,y} object would re-render the overlay for nothing.
    const moveTo = (x: number, y: number) => {
      setPos(p => (p && Math.abs(p.x - x) < 0.5 && Math.abs(p.y - y) < 0.5) ? p : { x, y });
    };
    // Anchor near the object center, nudged down-right of the GenStatusOverlay chip
    // (points at the work without covering the status text), then clamped so the whole
    // cursor footprint (svg + label ≈ 72×46) stays inside the artboard. The old
    // bottom-right-inside anchor put full-bleed backgrounds' cursor at the canvas
    // corner, where .artboard-shadow's overflow:hidden clipped it out of sight
    // (Dale 09-02 21:37 — SPEC-411).
    const CURSOR_W = 72, CURSOR_H = 46, EDGE = 4;
    const anchorOf = (o: CanvasObject) => ({
      x: Math.min(Math.max(EDGE, (o.x + o.w / 2 + 10) * scale), Math.max(EDGE, bounds.w * scale - CURSOR_W)),
      y: Math.min(Math.max(EDGE, (o.y + o.h / 2 + 6) * scale), Math.max(EDGE, bounds.h * scale - CURSOR_H)),
    });

    // Job transitions (queued/running → done/error) fire the pulse/shake exactly once.
    const prev = prevJobsRef.current;
    const next: Record<string, string> = {};
    let transition: 'done' | 'error' | null = null;
    for (const o of objects) {
      if (!o.job_id) continue;
      const st = jobs[o.job_id]?.status;
      if (st) next[o.job_id] = st;
      const was = prev[o.job_id];
      if (was && was !== 'done' && was !== 'error' && !transition) {
        if (st === 'done') transition = 'done';
        else if (st === 'error') transition = 'error';
      }
    }
    prevJobsRef.current = next;

    if (transition && !busyRef.current) {
      busyRef.current = true;
      setPhase(transition === 'done' ? 'pulse' : 'shake');
      schedule(transition === 'done' ? GHOST_PULSE_MS : GHOST_SHAKE_MS, () => { busyRef.current = false; setTick(n => n + 1); });
      return;
    }
    if (busyRef.current) return;

    // 1) Visit freshly born objects, oldest first, once per birth.
    const fresh = objects
      .filter(o => o._born && now - o._born <= GHOST_FRESH_MS && o.visible !== false && !visitedRef.current.has(`${o.id}:${o._born}`))
      .sort((a, b) => (a._born as number) - (b._born as number));
    if (fresh.length > 0) {
      const target = fresh[0];
      visitedRef.current.add(`${target.id}:${target._born}`);
      const a = anchorOf(target);
      moveTo(a.x, a.y);
      setPhase('visit');
      schedule(GHOST_DWELL_MS, () => setTick(n => n + 1));
      return;
    }

    // 2) Camp on a pending placeholder until its job resolves (JOB_UPDATE re-runs this
    //    effect; the landed object stops matching, so multiple pendings rotate naturally).
    const pending = objects.find(o => o.type === 'image' && o.pending && !o.error && o.visible !== false);
    if (pending) {
      const a = anchorOf(pending);
      moveTo(a.x, a.y);
      setPhase('camp');
      return;
    }

    // 3) Idle — fade out in place (pos retained; the opacity transition handles the exit).
    setPhase('hidden');
  }, [objects, jobs, scale, preview, tick, bounds]);

  if (preview) return null;
  const cls = 'ghost-cursor'
    + (phase !== 'hidden' && pos ? ' visible' : '')
    + (phase === 'pulse' ? ' pulse' : '')
    + (phase === 'shake' ? ' shake' : '');
  return (
    <div className={cls} style={pos ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined} aria-hidden="true">
      <div className="ghost-cursor-inner">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" fill="currentColor" />
          <path d="M13 13l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="ghost-cursor-label">{t('canvas.agentCursor')}</span>
      </div>
    </div>
  );
}

export interface ArtboardProps {
  canvas: CanvasState;
  selectedId: string | null;
  /** Multi-selection set */
  selectedIds: string[];
  jobs: Record<string, JobRecord>;
  onSelect: (id: string | null, additive?: boolean) => void;
  /** Marquee-selection result */
  onMultiSelect?: (ids: string[], additive: boolean) => void;
  onPatch: (id: string, patch: Partial<CanvasObject>) => void;
  /** Move many: dragging any selected object applies the delta to all selected (computed in the reducer) */
  onMoveMany?: (id: string, x: number, y: number) => void;
  stageRef: React.RefObject<Konva.Stage | null>;
  /** Region-select local editing: target image id (null = normal mode) */
  regionTarget?: string | null;
  /** Region-selection complete; rect in canvas coordinates relative to the target object */
  onRegionSelect?: (relRect: { x: number; y: number; w: number; h: number }) => void;
  /** Preview mode: not editable; clicking an object with link_to navigates pages */
  preview?: boolean;
  onNavigate?: (pageId: string) => void;
}

export default function Artboard({ canvas, selectedId, selectedIds, jobs, onSelect, onMultiSelect, onPatch, onMoveMany, stageRef, regionTarget, onRegionSelect, preview = false, onNavigate }: ArtboardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const dragRef = useRef<{ id: string; startX: number; startY: number } | null>(null);
  const [scale, setScale] = useState(0.5);
  // SPEC-468 M2: two-finger pinch-zoom + pan state (touch-only; the desktop mouse path is
  // untouched — G1). d0/scale0 = pinch-start distance & scale; lastC = latest centroid (client
  // px); anchor = artboard-space point that must stay under the centroid across scale commits.
  const pinchRef = useRef<{ d0: number; scale0: number; lastC: { x: number; y: number }; anchor: { x: number; y: number } } | null>(null);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  // Blank-area marquee selection (independent from the region-edit marquee)
  const [selM, setSelM] = useState<{ x0: number; y0: number; x1: number; y1: number; additive: boolean } | null>(null);
  const theme = useTheme(); // theme toggle → re-read token colors
  const t = useT();
  // Shift+drag = start a marquee from anywhere (including over an object).
  // Helps when a full canvas leaves no blank space to start from; while Shift is held objects are not
  // draggable, avoiding a race with drag-to-move.
  const [shiftHeld, setShiftHeld] = useState(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftHeld(true); };
    const up = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftHeld(false); };
    const blur = () => setShiftHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', blur); };
  }, []);

  const regionObj = regionTarget ? canvas.objects.find(o => o.id === regionTarget && o.type === 'image') ?? null : null;

  const clampToObj = (px: number, py: number) => {
    if (!regionObj) return { x: px, y: py };
    return {
      x: Math.max(regionObj.x, Math.min(regionObj.x + regionObj.w, px)),
      y: Math.max(regionObj.y, Math.min(regionObj.y + regionObj.h, py)),
    };
  };

  const stagePointer = () => {
    const stage = stageRef.current;
    if (!stage) return null;
    const p = stage.getPointerPosition();
    if (!p) return null;
    return { x: p.x / scale, y: p.y / scale };
  };

  const onRegionMouseDown = () => {
    const p = stagePointer();
    if (!p || !regionObj) return;
    const c = clampToObj(p.x, p.y);
    setMarquee({ x0: c.x, y0: c.y, x1: c.x, y1: c.y });
  };
  const onRegionMouseMove = () => {
    if (!marquee) return;
    const p = stagePointer();
    if (!p) return;
    const c = clampToObj(p.x, p.y);
    setMarquee(m => m ? { ...m, x1: c.x, y1: c.y } : m);
  };
  const onRegionMouseUp = () => {
    if (!marquee || !regionObj) { setMarquee(null); return; }
    const x = Math.min(marquee.x0, marquee.x1), y = Math.min(marquee.y0, marquee.y1);
    const w = Math.abs(marquee.x1 - marquee.x0), h = Math.abs(marquee.y1 - marquee.y0);
    setMarquee(null);
    if (w * scale > 8 && h * scale > 8 && onRegionSelect) {
      onRegionSelect({ x: x - regionObj.x, y: y - regionObj.y, w, h });
    }
  };

  // ===== Blank-area marquee selection =====
  const onSelMouseDown = (e: any) => {
    if (preview) return;
    const p = stagePointer();
    if (!p) return;
    setSelM({ x0: p.x, y0: p.y, x1: p.x, y1: p.y, additive: !!e.evt?.shiftKey });
  };
  const onSelMouseMove = () => {
    if (!selM) return;
    const p = stagePointer();
    if (!p) return;
    setSelM(m => m ? { ...m, x1: p.x, y1: p.y } : m);
  };
  const onSelMouseUp = () => {
    if (!selM) return;
    const x = Math.min(selM.x0, selM.x1), y = Math.min(selM.y0, selM.y1);
    const w = Math.abs(selM.x1 - selM.x0), h = Math.abs(selM.y1 - selM.y0);
    const additive = selM.additive;
    setSelM(null);
    if (w * scale < 6 && h * scale < 6) {
      // Treat as blank click: clear selection unless additive (keeps prior behavior)
      if (!additive) onSelect(null);
      return;
    }
    let ids = canvas.objects
      .filter(o => o.visible !== false && o.x < x + w && o.x + o.w > x && o.y < y + h && o.y + o.h > y)
      .map(o => o.id);
    // Marquee hitting any member of a group selects the whole group (same semantics as single click);
    // hidden members are never selected (SPEC-405)
    const hitGids = new Set(
      canvas.objects.filter(o => ids.includes(o.id) && o.group_id).map(o => o.group_id as string)
    );
    if (hitGids.size > 0) {
      const expanded = new Set(ids);
      for (const o of canvas.objects) {
        if (o.group_id && hitGids.has(o.group_id) && o.visible !== false) expanded.add(o.id);
      }
      ids = [...expanded];
    }
    if (onMultiSelect) onMultiSelect(ids, additive);
    else if (ids.length === 1) onSelect(ids[0]);
  };

  // ===== SPEC-468 M2: touch surface for the Stage-level interactions (binding layer only) =====
  // The region/blank marquees were mouse-only (Stage onMouseDown/Move/Up); Konva does not fire
  // those on touch. The mirrors below reuse the exact same handlers and stagePointer() (Konva's
  // getPointerPosition covers touch). Object tap/drag needed no patch: Konva onTap + draggable
  // are touch-native (proved by t3-touch-pre.json). Two-finger pinch zooms the SAME scale state
  // the fit effect owns (clamp widened to 3 for mobile detail viewing); pan scrolls the wrap
  // (mobile CSS makes it overflow:auto with scroll-safe auto-margin centering).
  const touchCenter = (ts: TouchList) => ({ x: (ts[0].clientX + ts[1].clientX) / 2, y: (ts[0].clientY + ts[1].clientY) / 2 });
  const touchDist = (ts: TouchList) => Math.hypot(ts[0].clientX - ts[1].clientX, ts[0].clientY - ts[1].clientY);
  const pinchAnchor = (c: { x: number; y: number }) => {
    const wrap = containerRef.current;
    const shadow = wrap?.firstElementChild as HTMLElement | null;
    if (!wrap || !shadow) return { x: 0, y: 0 };
    const rect = wrap.getBoundingClientRect();
    return {
      x: (wrap.scrollLeft + (c.x - rect.left) - shadow.offsetLeft) / scale,
      y: (wrap.scrollTop + (c.y - rect.top) - shadow.offsetTop) / scale,
    };
  };
  const stopObjectDrag = () => {
    // Second finger lands mid-object-drag: pinch takes over — stop Konva DD so the object does
    // not chase finger 1 while zooming (dragend still commits the tiny move; acceptable, same
    // as an aborted quick drag).
    const stage = stageRef.current;
    if (!stage) return;
    for (const o of canvas.objects) {
      const n = stage.findOne(`#${CSS.escape(o.id)}`) as unknown as { isDragging?: () => boolean; stopDrag?: () => void } | null;
      if (n?.isDragging?.()) n.stopDrag?.();
    }
  };
  const onStageTouchStart = (e: any) => {
    const ts = e.evt.touches;
    if (ts.length >= 2) {
      setMarquee(null); setSelM(null);
      stopObjectDrag();
      const c = touchCenter(ts);
      pinchRef.current = { d0: touchDist(ts), scale0: scale, lastC: c, anchor: pinchAnchor(c) };
      e.evt.preventDefault();
      return;
    }
    if (regionObj) { onRegionMouseDown(); return; }
    if (e.target === e.target.getStage() || e.target.name() === 'bg') onSelMouseDown(e);
  };
  const onStageTouchMove = (e: any) => {
    const ts = e.evt.touches;
    const p = pinchRef.current;
    if (p && ts.length >= 2) {
      e.evt.preventDefault();
      const wrap = containerRef.current;
      const c = touchCenter(ts);
      if (wrap) {
        wrap.scrollLeft -= c.x - p.lastC.x; // two-finger pan = centroid delta
        wrap.scrollTop -= c.y - p.lastC.y;
        p.anchor = pinchAnchor(c);          // re-anchor under the moved centroid (pre-commit scale)
        p.lastC = c;
      }
      const s2 = Math.max(0.05, Math.min(3, p.scale0 * (touchDist(ts) / p.d0)));
      if (Math.abs(s2 - scale) >= 0.002) setScale(s2);
      return;
    }
    if (ts.length !== 1) return;
    if (regionObj) onRegionMouseMove();
    else if (selM) onSelMouseMove();
  };
  const onStageTouchEnd = (e: any) => {
    if (pinchRef.current && e.evt.touches.length < 2) pinchRef.current = null;
    if (e.evt.touches.length !== 0) return;
    if (regionObj) onRegionMouseUp();
    else if (selM) onSelMouseUp();
  };

  const colors = useMemo(() => ({
    accent: cssColor('--accent', '#c2442b'),
    danger: cssColor('--danger', '#b3261e'),
  }), [theme]);

  const { w: AW, h: AH } = canvas.artboard;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fit = () => {
      const s = Math.min((el.clientWidth - 48) / AW, (el.clientHeight - 48) / AH);
      setScale(Math.max(0.05, Math.min(1, s)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [AW, AH]);

  // SPEC-468 M2: keep the pinched artboard point under the centroid across scale commits —
  // runs after layout, reading the NEW .artboard-shadow offsets (auto-margin centering shifts
  // as the shadow resizes). No-op unless a pinch is in flight.
  useEffect(() => {
    const p = pinchRef.current;
    const wrap = containerRef.current;
    if (!p || !wrap) return;
    const shadow = wrap.firstElementChild as HTMLElement | null;
    if (!shadow) return;
    const rect = wrap.getBoundingClientRect();
    wrap.scrollLeft = shadow.offsetLeft + p.anchor.x * scale - (p.lastC.x - rect.left);
    wrap.scrollTop = shadow.offsetTop + p.anchor.y * scale - (p.lastC.y - rect.top);
  }, [scale]);

  // Redraw once webfonts are ready — otherwise CJK is measured with fallback fonts and line breaks land wrong
  useEffect(() => {
    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) stageRef.current?.batchDraw();
    });
    return () => { alive = false; };
  }, [stageRef]);

  useEffect(() => {
    const tr = trRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    // Transformer appears only for single selection; multi-selection uses dashed outlines.
    // SPEC-405: hidden objects never get a transformer (they are not rendered/hit-testable).
    const selObj = canvas.objects.find(o => o.id === selectedId);
    const single = !preview && selectedId && selectedIds.length <= 1 && selObj?.visible !== false;
    const node = single ? stage.findOne(`#${CSS.escape(selectedId)}`) : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, selectedIds, canvas.objects, stageRef, preview]);

  const common = useMemo(() => ({
    draggable: !preview && !shiftHeld,
    listening: !preview,
    onDragStart: (e: any, id: string) => {
      dragRef.current = { id, startX: e.target.x(), startY: e.target.y() };
    },
    onDragMove: (e: any, id: string) => {
      const d = dragRef.current;
      if (!d) return;
      const stage = stageRef.current;
      if (!stage) return;
      const dx = e.target.x() - d.startX, dy = e.target.y() - d.startY;
      const shift = (oid: string) => {
        const o = canvas.objects.find(x => x.id === oid);
        const n = stage.findOne(`#${CSS.escape(oid)}`);
        if (o && n) { n.x(o.x + dx); n.y(o.y + dy); }
      };
      // Multi-select linkage: dragging one selected object moves the others by the same delta in real time
      if (selectedIds.length > 1 && selectedIds.includes(id)) {
        for (const oid of selectedIds) if (oid !== id) shift(oid);
      }
      // SPEC-425: dragging a frame live-translates its whole subtree (committed on drag end
      // via PATCH_OBJECT → resolveLayout; this is only the in-flight visual follow)
      const draggedObj = canvas.objects.find(x => x.id === id);
      if (draggedObj?.type === 'frame') {
        for (const did of collectDescendants(canvas.objects, id)) {
          if (selectedIds.length > 1 && selectedIds.includes(did)) continue; // already shifted above
          shift(did);
        }
      }
    },
    onDragEnd: (e: any, id: string) => {
      dragRef.current = null;
      if (selectedIds.length > 1 && selectedIds.includes(id) && onMoveMany) {
        onMoveMany(id, e.target.x(), e.target.y());
        return;
      }
      onPatch(id, { x: e.target.x(), y: e.target.y() });
    },
    onTransformEnd: (e: any, id: string) => {
      const n = e.target;
      const sx = n.scaleX(), sy = n.scaleY();
      onPatch(id, {
        x: n.x(), y: n.y(),
        w: Math.max(20, n.width() * sx),
        h: Math.max(20, n.height() * sy),
        rotation: n.rotation(),
      });
      n.scaleX(1); n.scaleY(1);
    },
    onClick: (e: any, id: string) => onSelect(id, !!e.evt?.shiftKey),
    onTap: (e: any, id: string) => onSelect(id, !!e.evt?.shiftKey),
  }), [onPatch, onSelect, onMoveMany, preview, shiftHeld, selectedIds, canvas.objects, stageRef]);

  // Preview mode: only objects with a navigation link are clickable (cursor hint)
  const previewHandlers = useMemo(() => ({
    onClick: (_e: any, obj: CanvasObject) => { if (obj.link_to && onNavigate) onNavigate(obj.link_to); },
    onTap: (_e: any, obj: CanvasObject) => { if (obj.link_to && onNavigate) onNavigate(obj.link_to); },
    onMouseEnter: (e: any, obj: CanvasObject) => {
      const stage = e.target.getStage();
      if (stage) stage.container().style.cursor = obj.link_to ? 'pointer' : 'default';
    },
    onMouseLeave: (e: any) => {
      const stage = e.target.getStage();
      if (stage) stage.container().style.cursor = 'default';
    },
  }), [onNavigate]);

  const pendingObjs = canvas.objects.filter(o => o.type === 'image' && (o.pending || !o.src || o.error) && o.visible !== false);

  return (
    <div ref={containerRef} className="artboard-wrap">
      <div className="artboard-shadow" style={{ width: AW * scale, height: AH * scale }}>
        <Stage
          ref={stageRef as any}
          width={AW * scale}
          height={AH * scale}
          scaleX={scale}
          scaleY={scale}
          onMouseDown={regionObj ? onRegionMouseDown : (e) => { if (e.evt?.shiftKey || e.target === e.target.getStage() || e.target.name() === 'bg') onSelMouseDown(e); }}
          onMouseMove={regionObj ? onRegionMouseMove : (selM ? onSelMouseMove : undefined)}
          onMouseUp={regionObj ? onRegionMouseUp : (selM ? onSelMouseUp : undefined)}
          onTouchStart={onStageTouchStart}
          onTouchMove={onStageTouchMove}
          onTouchEnd={onStageTouchEnd}
        >
          <Layer>
            <Rect name="bg" x={0} y={0} width={AW} height={AH} fill={canvas.artboard.bg} />
            {canvas.objects.map(obj =>
              obj.type === 'frame' ? (
                // SPEC-425: auto-layout container — transparent by default (fill undefined ⇒ nothing
                // painted on the scene canvas); Konva's fillEnabled defaults to true, so the plain
                // geometry still paints the hit canvas → selectable/draggable without a visible fill.
                <Rect
                  key={obj.id}
                  id={obj.id}
                  x={obj.x} y={obj.y} width={obj.w} height={obj.h}
                  fill={obj.fill}
                  stroke={obj.stroke}
                  strokeWidth={obj.stroke_width ?? 0}
                  cornerRadius={obj.radius ?? 0}
                  rotation={obj.rotation ?? 0}
                  visible={obj.visible !== false}
                  draggable={!preview && !shiftHeld}
                  listening={preview ? !!obj.link_to : true}
                  onDragStart={(e) => common.onDragStart(e, obj.id)}
                  onDragMove={(e) => common.onDragMove(e, obj.id)}
                  onDragEnd={(e) => common.onDragEnd(e, obj.id)}
                  onTransformEnd={(e) => common.onTransformEnd(e, obj.id)}
                  onClick={preview ? (e) => previewHandlers.onClick(e, obj) : (e) => common.onClick(e, obj.id)}
                  onTap={preview ? (e) => previewHandlers.onTap(e, obj) : (e) => common.onTap(e, obj.id)}
                  onMouseEnter={preview ? (e) => previewHandlers.onMouseEnter(e, obj) : undefined}
                  onMouseLeave={preview ? (e) => previewHandlers.onMouseLeave(e) : undefined}
                />
              ) : obj.type === 'image' ? (
                <ImageNode key={obj.id} obj={obj} colors={colors} common={preview ? {
                  id: obj.id,
                  draggable: false,
                  listening: !!obj.link_to,
                  onClick: (e: any) => previewHandlers.onClick(e, obj),
                  onTap: (e: any) => previewHandlers.onTap(e, obj),
                  onMouseEnter: (e: any) => previewHandlers.onMouseEnter(e, obj),
                  onMouseLeave: (e: any) => previewHandlers.onMouseLeave(e),
                } : obj.id === regionTarget ? {
                  id: obj.id,
                  draggable: false,
                  listening: false,
                } : {
                  id: obj.id,
                  draggable: !shiftHeld,
                  onDragStart: (e: any) => common.onDragStart(e, obj.id),
                  onDragMove: (e: any) => common.onDragMove(e, obj.id),
                  onDragEnd: (e: any) => common.onDragEnd(e, obj.id),
                  onTransformEnd: (e: any) => common.onTransformEnd(e, obj.id),
                  onClick: (e: any) => common.onClick(e, obj.id),
                  onTap: (e: any) => common.onTap(e, obj.id),
                }} />
              ) : obj.type === 'rect' ? (
                <Rect
                  key={obj.id}
                  id={obj.id}
                  x={obj.x} y={obj.y} width={obj.w} height={obj.h}
                  fill={obj.fill ?? 'rgba(128,128,128,0.12)'}
                  stroke={obj.stroke ?? 'transparent'}
                  strokeWidth={obj.stroke_width ?? 0}
                  cornerRadius={obj.radius ?? 8}
                  rotation={obj.rotation ?? 0}
                  visible={obj.visible !== false}
                  draggable={!preview && !shiftHeld}
                  listening={preview ? !!obj.link_to : true}
                  onDragStart={(e) => common.onDragStart(e, obj.id)}
                  onDragMove={(e) => common.onDragMove(e, obj.id)}
                  onDragEnd={(e) => common.onDragEnd(e, obj.id)}
                  onTransformEnd={(e) => common.onTransformEnd(e, obj.id)}
                  onClick={preview ? (e) => previewHandlers.onClick(e, obj) : (e) => common.onClick(e, obj.id)}
                  onTap={preview ? (e) => previewHandlers.onTap(e, obj) : (e) => common.onTap(e, obj.id)}
                  onMouseEnter={preview ? (e) => previewHandlers.onMouseEnter(e, obj) : undefined}
                  onMouseLeave={preview ? (e) => previewHandlers.onMouseLeave(e) : undefined}
                />
              ) : obj.type === 'icon' ? (
                /* SPEC-457 M1: 单色矢量图标 — bundled path 数据（零外部请求），24 网格缩放至 w×h；
                   stroke=color（缺省继承文字默认色）；fill=transparent 只为打开命中区域（frame 分支
                   同思路：几何进 hit canvas），选中/拖拽/隐藏/命名全走既有对象管线。
                   SPEC-462 M3: strokeScaleEnabled=false — strokeWidth 为屏幕 px（不随 scaleX/Y 缩放），
                   墨迹足迹≈几何盒±stroke/2 恒含内（截图/导出 pixelRatio=1 下与 doc 单位一致）。
                   SPEC-464 M1.3（E1 根治）: 恒定 2px → 光学曲线 iconStrokePx(w)（clamp(2×√(w/24),
                   1.25, 3) 量化 0.25 — 小图标相对加粗/大图标相对收细，24 档=2.0 非回归锚点）。
                   墨迹含内（inset = w×2/24 ≥ stroke/2）单测 pin — M3 grounded 仲裁结论保全且更准。
                   SPEC-465 M1: data 过 normalizePathData —— Konva parsePathData 对 compact arc-flag
                   （如 leaf `a10 10 0 0010-10`）误 tokenize 致巨弧拖尾；归一化对任何 path 来源生效。 */
                <Path
                  key={obj.id}
                  id={obj.id}
                  x={obj.x} y={obj.y}
                  data={normalizePathData(ICON_PATHS[obj.icon ?? ''] ?? '')}
                  scaleX={obj.w / 24}
                  scaleY={obj.h / 24}
                  fill="transparent"
                  stroke={obj.color ?? '#1a1a1a'}
                  strokeWidth={iconStrokePx(obj.w)}
                  strokeScaleEnabled={false}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  rotation={obj.rotation ?? 0}
                  visible={obj.visible !== false}
                  draggable={!preview && !shiftHeld}
                  listening={preview ? !!obj.link_to : true}
                  onDragStart={(e) => common.onDragStart(e, obj.id)}
                  onDragMove={(e) => common.onDragMove(e, obj.id)}
                  onDragEnd={(e) => common.onDragEnd(e, obj.id)}
                  onTransformEnd={(e) => common.onTransformEnd(e, obj.id)}
                  onClick={preview ? (e) => previewHandlers.onClick(e, obj) : (e) => common.onClick(e, obj.id)}
                  onTap={preview ? (e) => previewHandlers.onTap(e, obj) : (e) => common.onTap(e, obj.id)}
                  onMouseEnter={preview ? (e) => previewHandlers.onMouseEnter(e, obj) : undefined}
                  onMouseLeave={preview ? (e) => previewHandlers.onMouseLeave(e) : undefined}
                />
              ) : (
                <Text
                  key={obj.id}
                  id={obj.id}
                  text={obj.text ?? ''}
                  x={obj.x} y={obj.y} width={obj.w}
                  fontSize={obj.font_size ?? 48}
                  lineHeight={LINE_HEIGHT_RATIO}
                  fontFamily={obj.font_family ? `"${obj.font_family}", ${CANVAS_FONT}` : CANVAS_FONT}
                  fill={obj.color ?? '#1a1a1a'}
                  fontStyle={obj.font_weight === 'bold' ? 'bold' : 'normal'}
                  align={obj.align ?? 'left'}
                  rotation={obj.rotation ?? 0}
                  visible={obj.visible !== false}
                  draggable={!preview && !shiftHeld}
                  listening={preview ? !!obj.link_to : true}
                  onDragStart={(e) => common.onDragStart(e, obj.id)}
                  onDragMove={(e) => common.onDragMove(e, obj.id)}
                  onDragEnd={(e) => common.onDragEnd(e, obj.id)}
                  onTransformEnd={(e) => common.onTransformEnd(e, obj.id)}
                  onClick={preview ? (e) => previewHandlers.onClick(e, obj) : (e) => common.onClick(e, obj.id)}
                  onTap={preview ? (e) => previewHandlers.onTap(e, obj) : (e) => common.onTap(e, obj.id)}
                  onMouseEnter={preview ? (e) => previewHandlers.onMouseEnter(e, obj) : undefined}
                  onMouseLeave={preview ? (e) => previewHandlers.onMouseLeave(e) : undefined}
                />
              )
            )}
            {/* Multi-selection outlines (dashed, monochrome) */}
            {!preview && selectedIds.length > 1 && canvas.objects.filter(o => selectedIds.includes(o.id) && o.visible !== false).map(o => (
              <Rect
                key={`sel-${o.id}`}
                x={o.x - 3} y={o.y - 3} width={o.w + 6} height={o.h + 6}
                stroke={colors.accent}
                strokeWidth={1.5 / scale}
                dash={[6 / scale, 4 / scale]}
                cornerRadius={4}
                listening={false}
              />
            ))}
            {/* Navigation-link corner badge (visible in edit mode; monochrome SVG path, no glyph icons) */}
            {!preview && canvas.objects.filter(o => o.link_to && o.visible !== false).map(o => (
              <Path
                key={`link-${o.id}`}
                x={o.x + o.w - 22}
                y={o.y + 7}
                data="M0 5 H10 M6 1 L10 5 L6 9"
                stroke={colors.accent}
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                listening={false}
              />
            ))}
            <Transformer
              ref={trRef}
              rotateEnabled
              keepRatio={false}
              anchorSize={8}
              anchorCornerRadius={4}
              borderStroke={colors.accent}
              anchorStroke={colors.accent}
            />
            {regionObj && marquee && (
              <Rect
                x={Math.min(marquee.x0, marquee.x1)}
                y={Math.min(marquee.y0, marquee.y1)}
                width={Math.abs(marquee.x1 - marquee.x0)}
                height={Math.abs(marquee.y1 - marquee.y0)}
                stroke={colors.accent}
                strokeWidth={1.5 / scale}
                dash={[6 / scale, 4 / scale]}
                fill="rgba(74, 222, 128, 0.12)"
                listening={false}
              />
            )}
            {!regionObj && selM && (
              <>
                {/* Marquee visibility: translucent accent fill + dashed stroke, split into two layers so the opacity does not fade the stroke too */}
                <Rect
                  x={Math.min(selM.x0, selM.x1)}
                  y={Math.min(selM.y0, selM.y1)}
                  width={Math.abs(selM.x1 - selM.x0)}
                  height={Math.abs(selM.y1 - selM.y0)}
                  fill={colors.accent}
                  opacity={0.1}
                  listening={false}
                />
                <Rect
                  x={Math.min(selM.x0, selM.x1)}
                  y={Math.min(selM.y0, selM.y1)}
                  width={Math.abs(selM.x1 - selM.x0)}
                  height={Math.abs(selM.y1 - selM.y0)}
                  stroke={colors.accent}
                  strokeWidth={1.5 / scale}
                  dash={[5 / scale, 4 / scale]}
                  listening={false}
                />
              </>
            )}
          </Layer>
        </Stage>
        {pendingObjs.map(obj => (
          <GenStatusOverlay key={obj.id} obj={obj} job={obj.job_id ? jobs[obj.job_id] : undefined} scale={scale} />
        ))}
        <GhostCursor objects={canvas.objects} jobs={jobs} scale={scale} preview={preview} bounds={canvas.artboard} />
        {/* The region-drag hint moved to the bottom of the canvas-area in App.tsx (the original mount point got clipped by the canvas container) */}
      </div>
    </div>
  );
}
